import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, X, Rocket, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const STEPS = [
  { key: "created_project", label: "Create your first project", cta: "Create your first project", tab: null },
  { key: "uploaded_blueprint", label: "Upload a blueprint", cta: "Upload a blueprint", tab: "takeoff" },
  { key: "calibrated", label: "Calibrate the drawing scale", cta: "Calibrate the scale", tab: "takeoff" },
  { key: "estimated", label: "Build an estimate", cta: "Build your estimate", tab: "estimate" },
  { key: "billed", label: "Raise a progress billing / application", cta: "Raise an application", tab: "billing" },
];

export const OnboardingChecklist = ({ projects = [], onCreateProject }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const storageKey = `bs_onboarding_dismissed:${user?.tenant_id || "anon"}`;
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(storageKey) === "1");
  }, [storageKey]);

  useEffect(() => {
    api.get("/onboarding/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  if (dismissed || !status || status.all_done) return null;
  const done = STEPS.filter((s) => status.steps[s.key]).length;
  const next = STEPS.find((s) => !status.steps[s.key]);

  const dismiss = () => { localStorage.setItem(storageKey, "1"); setDismissed(true); };

  const goToStep = (step) => {
    if (!step) return;
    track("onboarding_cta_clicked", { step: step.key });
    // First step, or no project yet -> kick off project creation
    if (step.key === "created_project" || projects.length === 0) {
      if (onCreateProject) onCreateProject();
      else navigate("/app");
      return;
    }
    const latest = projects.find((p) => p.status === "active") || projects[0];
    if (!latest) {
      if (onCreateProject) onCreateProject();
      return;
    }
    navigate(`/app/project/${latest.id}${step.tab ? `?tab=${step.tab}` : ""}`);
  };

  const startTour = () => {
    track("onboarding_tour_started");
    const latest = projects.find((p) => p.status === "active") || projects[0];
    if (!latest) { if (onCreateProject) onCreateProject(); return; }
    navigate(`/app/project/${latest.id}?tab=takeoff&tour=1`);
  };

  return (
    <div className="bg-gradient-to-br from-[#1E3A5F] to-blue-900 rounded-xl p-6 text-white relative overflow-hidden" data-testid="onboarding-checklist">
      <button onClick={dismiss} data-testid="onboarding-dismiss" className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors">
        <X className="h-5 w-5" />
      </button>
      <div className="flex items-center gap-2">
        <span className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center"><Rocket className="h-5 w-5" /></span>
        <div>
          <h3 className="font-display font-bold text-lg">Get set up on TradeOff</h3>
          <p className="text-sm text-white/70">{done} of {STEPS.length} steps complete — you're on your way.</p>
        </div>
      </div>
      <div className="mt-4 h-1.5 rounded-full bg-white/15 overflow-hidden">
        <div className="h-full rounded-full bg-white transition-all" style={{ width: `${(done / STEPS.length) * 100}%` }} />
      </div>
      <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
        {STEPS.map((s) => {
          const ok = status.steps[s.key];
          const isNext = next && s.key === next.key;
          return (
            <li key={s.key} className="flex items-center gap-2.5 text-sm" data-testid={`onboarding-step-${s.key}`}>
              {ok
                ? <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
                : <Circle className={`h-5 w-5 shrink-0 ${isNext ? "text-white" : "text-white/40"}`} />}
              <button
                type="button"
                onClick={() => !ok && goToStep(s)}
                disabled={ok}
                data-testid={`onboarding-step-link-${s.key}`}
                className={`text-left transition-colors ${ok ? "text-white/80 line-through cursor-default" : isNext ? "text-white font-semibold hover:text-blue-100" : "text-white/90 hover:text-white hover:underline"}`}
              >
                {s.label}
              </button>
            </li>
          );
        })}
      </ul>
      {next && (
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
          <button
            onClick={() => goToStep(next)}
            data-testid="onboarding-next-cta"
            className="inline-flex items-center gap-2 bg-white text-[#1E3A5F] font-semibold text-sm rounded-lg px-4 py-2.5 hover:bg-blue-50 transition-colors"
          >
            {next.cta} <ArrowRight className="h-4 w-4" />
          </button>
          {projects.length > 0 && (
            <button
              onClick={startTour}
              data-testid="onboarding-start-tour"
              className="inline-flex items-center gap-1.5 text-white/90 hover:text-white text-sm font-medium underline decoration-white/40 underline-offset-2"
            >
              <Sparkles className="h-4 w-4" /> Take the 60-second tour
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
